import { yupResolver } from "@hookform/resolvers/yup";
import * as BonuscardStepTracker from "@omni/services/tracking/trackers/tools/bonusCard/bonuscardStep.tracker";
import { AddressType } from "@omni/types/api/customer/cloud/shared/enums/addressType.enum";
import { AddressValidationStatus } from "@omni/types/api/customer/cloud/shared/enums/addressValidationStatus.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import * as yup from "yup";

import { SubscribeCustomerToBonusCardRequest } from "@/api/hooks/customerService.types";
import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { FormTouchedCallback } from "@/components/Form/RHF/Callbacks/formFieldCallbacks.component";
import { CustomerAddressFormBlock } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.container";
import { CustomerDefaultAddressValidationSchema } from "@/components/Form/RHF/FormBlocks/Address/addressFormBlock.schema";
import { CheckBoxField } from "@/components/Form/RHF/FormFields/CheckboxField/CheckBoxField.component";
import { DateOfBirthField } from "@/components/Form/RHF/FormFields/DateOfBirthField/dateOfBirthField.component";
import { OptionalDateOfBirthFieldSchema } from "@/components/Form/RHF/FormFields/DateOfBirthField/dateOfBirthField.schema";
import { StoreField } from "@/components/Form/RHF/FormFields/StoreField/storeField.container";
import { StoreSchema } from "@/components/Form/RHF/FormFields/StoreField/storeField.schema";
import { TextField } from "@/components/Form/RHF/FormFields/TextField/Textfield.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { Messages } from "@/components/General/Messages/messages.container";
import { getShopNames } from "@/redux/modules/api/data/stores/shopNames/shopNames.selector";
import {
    broadcastMessage,
    clearMessageById
} from "@/redux/modules/value/messages/messages.utils";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getEmptyAddress } from "@/utils/address/address";
import { BPOSTInvalidAddressMessageId } from "@/utils/address/validation.config";
import { parseISO } from "@/utils/date";
import { parseDateOfBirth } from "@/utils/dateOfBirth";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { OmniFormContextProvider } from "@/utils/forms/omniForm.provider";
import { padStart } from "@/utils/textUtils";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

const BonusCardRequestValidationSchema = yup
    .object({
        email: RHFUserValidationRules.emailWithBonusCardState,
        storeId: RHFGeneralValidationRules.storeId,
        optInBirthdayCard: yup.boolean().default(false)
    })
    .concat(StoreSchema)
    .concat(OptionalDateOfBirthFieldSchema)
    .concat(CustomerDefaultAddressValidationSchema);

type BonusCardRequestFormValues = yup.InferType<
    typeof BonusCardRequestValidationSchema
>;

export type RequestBonusCardCustomerModel = {
    firstName?: string;
    lastName?: string;
    email?: string;
    favouriteStoreId?: string;
    dateOfBirth?: string; // YYYY-MM-DD
};

export type RequestBonusCardAddress = Partial<{
    id?: string;
    street: string;
    number: string;
    zipCode: string;
    city: string;
    box: string;
    country: string;
    validationStatus: AddressValidationStatus;
}>;

type Props = {
    disclaimer?: string;
    customer?: RequestBonusCardCustomerModel;
    address?: RequestBonusCardAddress;
    formId: string;
    fallbackStoreId?: string;
    isLoading?: boolean;
    onSubmit: (request: SubscribeCustomerToBonusCardRequest) => Promise<void>;
};

const BonusCardRequest = ({
    disclaimer,
    customer,
    fallbackStoreId,
    address,
    formId,
    isLoading = false,
    onSubmit
}: Props) => {
    const { validate, isValidating } = useAddressValidationContext();

    const { t } = useGranite();

    const shopNames = useSelector(getShopNames, shallowEqual);

    const { dateOfBirthDay, dateOfBirthMonth, dateOfBirthYear } = useMemo(
        () => parseDateOfBirth(customer?.dateOfBirth),
        [customer?.dateOfBirth]
    );

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            firstName: customer?.firstName,
            lastName: customer?.lastName,
            dateOfBirth: customer?.dateOfBirth
                ? parseISO(customer.dateOfBirth)
                : undefined,
            dateOfBirthDay: customer?.dateOfBirth
                ? padStart(dateOfBirthDay, 2, "0")
                : undefined,
            dateOfBirthMonth: customer?.dateOfBirth
                ? padStart(dateOfBirthMonth, 2, "0")
                : undefined,
            dateOfBirthYear: customer?.dateOfBirth
                ? dateOfBirthYear
                : undefined,
            ...(address || getEmptyAddress(AddressType.Delivery)),
            email: customer?.email || undefined,
            optInBirthdayCard: false,
            storeId: customer?.favouriteStoreId || fallbackStoreId || undefined
        },
        resolver: yupResolver(BonusCardRequestValidationSchema)
    });

    const onFormTouched = () => {
        BonuscardStepTracker.trackStart();
    };

    const handleSubmit = async (formValues: BonusCardRequestFormValues) => {
        clearMessageById(BPOSTInvalidAddressMessageId);
        clearMessageById("bonuscard-subscribe-success");
        clearMessageById("bonuscard-subscribe-failure");
        const validationResult = await validate({
            id: address?.id,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            street: formValues.street,
            number: formValues.number,
            zipCode: formValues.zipCode,
            city: formValues.city,
            country: formValues.country,
            box: formValues.box,
            addressing: null,
            type: null,
            isBpostField: !!formValues.selected,
            currentStatus: AddressValidationStatus.Unknown
        });

        if (
            validationResult.validationStatus ===
            AddressValidationStatus.Invalid
        ) {
            broadcastMessage({
                id: BPOSTInvalidAddressMessageId,
                text: t("bpost.validation.address.invalid"),
                status: "warning",
                dismissible: true
            });
            return;
        }

        if (
            validationResult.validationStatus === AddressValidationStatus.Closed
        ) {
            return;
        }

        const values: SubscribeCustomerToBonusCardRequest = {
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            email: formValues.email,
            storeId:
                formValues.storeId ||
                customer?.favouriteStoreId ||
                fallbackStoreId,
            optInBirthdayCard:
                !!formValues.dateOfBirth && !!formValues.optInBirthdayCard,
            dateOfBirth: formValues.dateOfBirth?.toISOString(),
            address: {
                street: formValues.street,
                number: formValues.number,
                box: formValues.box ? formValues.box : undefined,
                zipCode: formValues.zipCode,
                city: formValues.city,
                country: formValues.country,
                validationStatus: validationResult.validationStatus || undefined
            }
        };

        onSubmit?.(values)
            .then(() => {
                BonuscardStepTracker.trackSubmit({
                    storeId: values.storeId,
                    email: values.email,
                    optInBirthdayCard: values.optInBirthdayCard
                });

                methods.reset(undefined, { keepDefaultValues: true });
            })
            .catch(() => {});
    };

    return (
        <>
            <Messages messageid={BPOSTInvalidAddressMessageId} />
            <Messages messageid={"bonuscard-subscribe-success"} />
            <Messages messageid={"bonuscard-subscribe-failure"} />
            <FormInfoProvider
                formId={formId}
                withNeverbounce={false}
            >
                {() => (
                    <OmniFormContextProvider {...methods}>
                        <form
                            id={formId}
                            onSubmit={methods.handleSubmit(handleSubmit)}
                        >
                            <FormTouchedCallback callback={onFormTouched} />

                            <CustomerAddressFormBlock<
                                BonusCardRequestFormValues,
                                AddressType.Default
                            >
                                addressType={AddressType.Default}
                                country={address?.country}
                                addressStatus={
                                    address?.validationStatus ||
                                    AddressValidationStatus.Unknown
                                }
                                skipValidation={!address}
                            />

                            <DateOfBirthField<BonusCardRequestFormValues>
                                required={false}
                            />
                            <Row>
                                <Col>
                                    <TextField<BonusCardRequestFormValues>
                                        name="email"
                                        label={t("general.form.email.label")}
                                        required
                                        placeholder={t(
                                            "general.form.email.placeholder"
                                        )}
                                        autoComplete={"email"}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <StoreField<BonusCardRequestFormValues>
                                        name="storeId"
                                        label={t("general.form.store.label")}
                                        placeholder={t(
                                            "general.form.store.placeholder"
                                        )}
                                        required
                                        storeList={shopNames}
                                        disabled={isLoading}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <CheckBoxField<BonusCardRequestFormValues>
                                        label={t(
                                            "general.bonusCardRequest.modal.optInBirthdayCard.label"
                                        )}
                                        required
                                        name="optInBirthdayCard"
                                        optionalIndication={false}
                                        requiredIndication={false}
                                    />
                                </Col>
                            </Row>
                            {disclaimer && (
                                <Row>
                                    <Col>
                                        <div
                                            className={cn(
                                                "tw:text-sm tw:font-light"
                                            )}
                                            dangerouslySetInnerHTML={{
                                                __html: disclaimer
                                            }}
                                        />
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col>
                                    <Button
                                        test="bonuscardRequest-button-submit"
                                        type={"submit"}
                                        loading={isLoading || isValidating}
                                    >
                                        {t(
                                            "general.bonuscardRequest.buttons.request"
                                        )}
                                    </Button>
                                </Col>
                            </Row>
                        </form>
                    </OmniFormContextProvider>
                )}
            </FormInfoProvider>
        </>
    );
};

export { BonusCardRequest };
